/**
 * Shared copy and date formatting for the rank heat table (WEB-005, widened by WEB-002).
 *
 * `METRIC_TOOLTIPS`' six header strings and `METRIC_CELL_TOOLTIPS`' fifteen per-cell strings are
 * verbatim copy from `components/rank-tracker.md § Theme rows and product rows carry the five
 * metric roll-ups` and its `§ The five per-cell tooltips, pinned` sub-table -- **this reverses**
 * the section AUT-369 shipped under the opposite title (*"Product and theme rows render `–` in all
 * six … with the tooltips amended to stop promising a roll-up"*). A theme row and a hero-product
 * row now render all five metric aggregates from `metrics.<key>.value`
 * (`decisions/a-metric-roll-up-carries-its-own-base.md`), so the roll-up clause the earlier revision
 * dropped from the first five header tooltips is restored -- and `Comp.` changes arithmetic from
 * the captured `combined total` to `average`, the one string the human ruled differently from the
 * captured design. `Date Added` reverses separately (AUT-382, after the two facts it needs --
 * a product's own `created_at` and a theme's own assignment date -- landed on the wire): a
 * product row and a theme row now render their own date via `formatDateAdded`, the same as a
 * keyword row, and the header tooltip is rewritten to cover all three levels. It still carries no
 * cell tooltip -- unlike the five metrics above, a date has no base to state. The `KPS` tooltip expands
 * the letters as **Keyword Priority Score**, never the captured design's *Keyword Pursuit Score*
 * (`glossary.md § KPS`).
 */

import type { MetricKey } from "@/types/rankTracker";

/** The one absence mark on this table -- U+2013, never the shipped `N/A`. */
export const NO_READING_LABEL = "–";

export const PRODUCT_COLUMN_TOOLTIP = "Tracked product with keywords grouped into themes";

export const METRIC_TOOLTIPS = {
  kps:
    "Keyword Priority Score — Recom's proprietary metric for evaluating keywords, scored " +
    "1–100. 70 and above is a strong keyword worth pursuing; below 40 is usually not worth " +
    "the effort. Roll-up rows (product and theme) show the average of the keywords they contain.",
  searchVolume:
    "Search volume — estimated monthly searches for the keyword on Amazon. Roll-up rows " +
    "(product and theme) show the combined total of the keywords they contain.",
  keywordSales:
    "Keyword sales — estimated monthly units sold attributable to the keyword. Roll-up rows " +
    "(product and theme) show the combined total of the keywords they contain.",
  competitors:
    "Number of competing products ranking on the keyword. Roll-up rows (product and theme) " +
    "show the average of the keywords they contain.",
  relevance:
    "Relevance of the keyword to this product, scored 1–100. Roll-up rows (product and theme) " +
    "show the average of the keywords they contain.",
  dateAdded:
    "When tracking began: for products, the date the product was added to Signal; for themes, " +
    "the date the theme was assigned to the product; for keywords, the date the keyword entered " +
    "Rank Tracker.",
} as const;

/** `${count} keyword` / `${count} keywords` -- the pluralisation every `METRIC_CELL_TOOLTIPS`
 * string applies to `{N}` (and, where a template gives it a unit, to `{n}`). */
function pluralizeKeywordCount(count: number): string {
  return `${count} keyword${count === 1 ? "" : "s"}`;
}

/** The shared `KPS` / `Comp.` / `Rel.` three-string family (§ The five per-cell tooltips, pinned):
 * identical arithmetic (a mean) and base, differing only in the noun the `n === 0` form names.
 * `KPS`, `Comp.` and `Rel.` share this by design -- three strings that differ by a word are three
 * strings to keep in step, not three to write separately. */
function meanCellTooltip(noNoun: string): (n: number, keywordCount: number) => string {
  return (n, keywordCount) => {
    if (n === 0) return `None of this row's ${pluralizeKeywordCount(keywordCount)} has ${noNoun}.`;
    if (n === keywordCount) return `Average of all ${pluralizeKeywordCount(keywordCount)} in this row.`;
    return `Average of ${n} of this row's ${pluralizeKeywordCount(keywordCount)}.`;
  };
}

/**
 * FR-6's per-cell tooltip, one function per roll-up metric. `n` is
 * `metrics.<metric>.keywordCount` -- that metric's own base; `keywordCount` is the row's own
 * `keywordCount` from the wire (`theme.keywordCount` on `ThemeRow`, `product.keywordCount` on
 * `ProductRow`) -- **never** `ThemeRow`'s `visibleKeywordCount` prop, which is the filtered count
 * behind the row's `N of M keywords` caption and would make a roll-up's stated base move with a
 * client-side filter while the figure itself did not
 * (`decisions/a-metric-roll-up-carries-its-own-base.md`). No entry for `dateAdded`: that column
 * carries no cell tooltip at either roll-up level, and `MetricKey` does not include it.
 */
export const METRIC_CELL_TOOLTIPS: Record<MetricKey, (n: number, keywordCount: number) => string> = {
  kps: meanCellTooltip("a KPS"),
  competitors: meanCellTooltip("a competitor count"),
  relevance: meanCellTooltip("a relevance score"),
  searchVolume: (n, keywordCount) => {
    if (n === 0) {
      return `None of this row's ${pluralizeKeywordCount(keywordCount)} has a search volume estimate.`;
    }
    if (n === keywordCount) {
      return `Combined total of all ${pluralizeKeywordCount(keywordCount)}' monthly search estimates.`;
    }
    return (
      `Combined total of ${n} of this row's ${pluralizeKeywordCount(keywordCount)}' monthly ` +
      `search estimates.`
    );
  },
  keywordSales: (n, keywordCount) => {
    if (n === 0) {
      return `None of this row's ${pluralizeKeywordCount(keywordCount)} has a keyword sales estimate.`;
    }
    if (n === keywordCount) {
      return `Combined total of all ${pluralizeKeywordCount(keywordCount)}' monthly sales estimates.`;
    }
    return (
      `Combined total of ${n} of this row's ${pluralizeKeywordCount(keywordCount)}' monthly ` +
      `sales estimates.`
    );
  },
};

const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/**
 * Splits an ISO calendar date `YYYY-MM-DD` into its numeric parts. Never `new Date(iso)`:
 * `new Date("2026-06-11")` parses as UTC midnight and draws the previous day west of Greenwich
 * (`components/rank-tracker.md § Target state -- the screen`).
 */
function splitIsoDate(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split("-").map((part) => Number(part));
  return { year, month, day };
}

/**
 * `days[i]` formatted as `${MonthAbbrev} ${dayOfMonth}`, e.g. `Sep 8` -- the day-column header
 * label, and the phrase a roll-up heat cell's tooltip reads the day from.
 */
export function formatDayLabel(iso: string): string {
  const { month, day } = splitIsoDate(iso);
  return `${MONTH_ABBREVIATIONS[month - 1]} ${day}`;
}

/**
 * `dateAdded` formatted as `Mar 4, 2026`, or the absence mark when there is nothing to format --
 * `dateAdded` is `null` on a keyword that predates a tracking date, on a product or theme with no
 * `created_at` recorded, and on the `No theme` bucket when every one of its keywords is `null` too.
 * Shared by `KeywordRow`, `ThemeRow` and `ProductRow` -- all three levels carry their own
 * `dateAdded` now (AUT-382).
 */
export function formatDateAdded(iso: string | null): string {
  if (!iso) {
    return NO_READING_LABEL;
  }
  const { year, month, day } = splitIsoDate(iso);
  return `${MONTH_ABBREVIATIONS[month - 1]} ${day}, ${year}`;
}

/** A keyword metric value, formatted precisely (no compaction) or the absence mark for `null`.
 * The wire never carries the `-1` pending-recalculation sentinel here -- the backend maps it to
 * `null` (FR-14) -- so this performs no sentinel check of its own. */
export function formatMetricValue(value: number | null): string {
  return value === null ? NO_READING_LABEL : value.toLocaleString();
}
